/**
 * Hook personalizado para gestionar productos en compras de importación.
 *
 * Este hook centraliza las operaciones relacionadas con productos dentro de una compra
 * de tipo importación, incluyendo agregar, actualizar, eliminar y obtener detalles de productos.
 * También gestiona los modales y sincroniza el estado con Redux.
 */
import { useDispatch, useSelector } from 'react-redux';
import ImportPurchaseService from '../../services/purchases/ImportPurchaseService';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import { RootState } from '../../store/store';
import {
  setProductToEdit,
  clearProductToEdit,
  closeAddProductModal,
  closeEditProductModal,
} from '../../store/slices/purchases/importPurchaseSlice';
import { useBasePurchase } from './useBasePurchase';
import useModals from '../useModals';
import useConfirmModal from '../useConfirmModal';

export const useImportPurchase = () => {
  const dispatch = useDispatch();
  const { show: showAlert } = useAlerts();
  const { openModal } = useModals();
  const confirm = useConfirmModal();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { addProduct, updateProduct, deleteProduct } = useBasePurchase();

  // Estado global de producto seleccionado para edición y visibilidad de modales
  const { productToEdit, showAddProductModal, showEditProductModal } = useSelector((state: RootState) => state.importPurchase);

  /**
   * Agrega un nuevo producto a una compra de importación.
   *
   * 📦 Envía los datos del producto al backend para su registro.
   * ✅ Si se guarda correctamente, se actualiza el store global y se cierra el modal.
   * ❌ En caso de error, se muestra un mensaje al usuario.
   *
   * @param product - Objeto con los datos del producto a registrar.
   */
  const handlerAddProduct = async (product: any) => {
    try {
      showLoadingIndicator();
      const response = await ImportPurchaseService.addProduct(product);
      console.log(response);
      if (response.statusCode === 201) {
        const { title, message, type, data } = response;

        addProduct(data);  // Actualiza Redux
        dispatch(closeAddProductModal()); // Cierra el modal
        openModal(type, { title, message }); // Muestra confirmación
      } else {
        showAlert("error", "Error", "No se pudo agregar el producto.");
      }
    } catch (error) {
      console.error("❌ Error al agregar producto:", error);
    } finally {
      hideLoadingIndicator();
    }
  };


  /**
   * Actualiza un producto existente en la compra de importación.
   *
   * 🔄 Envia los nuevos datos del producto al backend.
   * ✅ Si la respuesta es exitosa, actualiza el store global.
   * ❌ En caso de error, muestra alerta correspondiente.
   *
   * @param product - Objeto con los datos actualizados del producto.
   */
  const handlerUpdateProduct = async (product: any) => {
    try {
      showLoadingIndicator();
      const response = await ImportPurchaseService.updateProduct(product);
      console.log(response);

      if (response.statusCode === 200) {
        const { title, message, type, data } = response;
        updateProduct(data); // ✅ Actualiza en el store
        dispatch(closeEditProductModal()); // ⛔ Cierra modal de edición
        openModal(type, { title, message }); // ✅ Muestra mensaje
      } else {
        showAlert("error", "Error", "No se pudo actualizar el producto.");
      }
    } catch (error) {
      console.error("❌ Error al actualizar producto:", error);
      showAlert("error", "Error", "Ocurrió un error inesperado.");
    } finally {
      hideLoadingIndicator();
    }
  };

  /**
   * Elimina un producto asociado a la compra de importación.
   *
   * ❓ Pregunta confirmación al usuario antes de proceder.
   * ✅ Si la eliminación es exitosa, actualiza el store global y muestra alerta.
   * ❌ Si ocurre un error, muestra una alerta de error.
   *
   * @param id - ID del producto (detalle) a eliminar.
   */
  const handlerDeleteProduct = async (id: number) => {
    confirm({
      title: '¿Eliminar Producto?',
      message: '¿Estás seguro de que deseas eliminar el producto de la compra?',
      icon: 'error',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmArgs: id,
      onConfirm: async (id) => {
        try {
          showLoadingIndicator();
          const response = await ImportPurchaseService.deleteProduct(id);
          if (response.statusCode === 200) {
            const { title, message, type } = response;
            deleteProduct(id);
            showAlert(type, title, message);
          } else {
            showAlert('error', 'Error', 'No se pudo eliminar el producto.');
          }
        } catch (error) {
          console.error('❌ Error al eliminar producto:', error);
          showAlert('error', 'Error', 'Ocurrió un error inesperado.');
        } finally {
          hideLoadingIndicator();
        }
      },
    });
  };

   /**
   * Obtiene un producto por su ID (detalle) para edición.
   *
   * 📥 Solicita el producto al backend por su `detailId`.
   * 🧠 Al obtenerlo, lo guarda en el estado global (`productToEdit`) para mostrar en el modal de edición.
   *
   * @param detailId - ID del detalle del producto a obtener.
   * @returns El objeto del producto (si existe).
   */
  const handlerGetProductById = async (detailId: number) => {
    try {
      showLoadingIndicator();
      const response = await ImportPurchaseService.getProductById(detailId);
      if (response?.statusCode === 200) {
        const { data } = response;
        console.log(data)
        dispatch(setProductToEdit(data)); // ⬅️ Lo guardamos en el estado
        return data;
      } else {
        showAlert("error", "Error", "No se pudo obtener el producto.");
      }
    } catch (error) {
      console.error("❌ Error al obtener producto:", error);
      showAlert("error", "Error", "Ocurrió un error inesperado.");
    } finally {
      hideLoadingIndicator();
    }
  };

  return {
    // 📤 Métodos para CRUD de productos
    handlerAddProduct,
    handlerUpdateProduct,
    handlerDeleteProduct,
    handlerGetProductById,

    // 🧠 Estado y acciones globales
    productToEdit,
    clearProductToEdit,
    showAddProductModal,
    showEditProductModal,
  };
};
